/**
 * AI 一括登録の入力画面
 *
 * テーマ名か公式サイトの URL のどちらかを渡す。この時点では Google の枠は消費しない。
 * テーマ名の欄は改行で 2 行以上入れると、AI を通さずその行をそのままスポット名として使う。
 */

import { Loader2, Sparkles } from 'lucide-react'
import { useState } from 'react'

import type { ImportSourceType } from '@/api/types/stamp-rally'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'

import { ImportSearchAffixFields } from './import-search-affix-fields'

type Props = {
  isSubmitting: boolean
  onSubmit: (sourceType: ImportSourceType, sourceInput: string, searchPrefix: string, searchSuffix: string) => void
}

const toLines = (value: string): string[] =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

export const ImportSourceForm = ({ isSubmitting, onSubmit }: Props) => {
  const [tab, setTab] = useState<'theme' | 'url'>('theme')
  const [theme, setTheme] = useState('')
  const [url, setUrl] = useState('')
  const [searchPrefix, setSearchPrefix] = useState('')
  const [searchSuffix, setSearchSuffix] = useState('')

  const lines = toLines(theme)
  const isList = lines.length > 1

  const sourceType: ImportSourceType = tab === 'url' ? 'ai_url' : isList ? 'text_list' : 'ai_theme'
  const value = tab === 'url' ? url.trim() : lines.join('\n')
  const canSubmit = value.length > 0 && !isSubmitting

  return (
    <div className="space-y-5 p-4">
      <Tabs value={tab} onValueChange={(next) => setTab(next as 'theme' | 'url')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="theme">テーマ名・名前リスト</TabsTrigger>
          <TabsTrigger value="url">URL から取り込む</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'theme' ? (
        <div className="space-y-2">
          <Label htmlFor="import-theme">テーマ名、または 1 行 1 件のスポット名</Label>
          <Textarea
            id="import-theme"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            rows={5}
            placeholder={
              '日本100名城、道の駅スタンプラリー 山梨県 など\n\n名前が分かっているなら 1 行 1 件で並べても取り込めます'
            }
          />
          {isList ? (
            <p className="text-xs text-muted-foreground">
              {lines.length} 行をそのままスポット名として取り込みます（AI は使いません）。
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              AI の知識だけで作るため、60 件を超えるラリーは取り込めません。件数が多いものは URL 指定を使ってください。
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="import-url">公式サイトの URL</Label>
          <Input
            id="import-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/stamp-rally/list"
            inputMode="url"
          />
          <p className="text-xs text-muted-foreground">
            スポット一覧が載っているページを指定してください。300 件を超えるページは都道府県別などに分けて取り込みます。
          </p>
        </div>
      )}

      <ImportSearchAffixFields
        prefix={searchPrefix}
        suffix={searchSuffix}
        onPrefixChange={setSearchPrefix}
        onSuffixChange={setSearchSuffix}
        idPrefix="import-source"
      />

      <Button
        className="w-full"
        disabled={!canSubmit}
        onClick={() => onSubmit(sourceType, value, searchPrefix.trim(), searchSuffix.trim())}
      >
        {isSubmitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Sparkles className="mr-2 size-4" />}
        スポットを探す
      </Button>

      <p className="text-xs text-muted-foreground">
        まずスポットの一覧を作ります。地図に載せるための場所検索は、次の画面であなたが選んだ分だけ実行します。
      </p>
    </div>
  )
}
